import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Loader2, Download, Share2, Youtube, Trash2, PlayCircle, Film,
  Clock, CheckCircle2, XCircle, Link2, Unlink, ExternalLink,
} from "lucide-react";

function fileHost() { return API.replace(/\/api$/, ""); }

const STATUS_LABEL = {
  queued: "क़तार में…",
  generating_vo: "आवाज़ generate हो रही है…",
  generating_clips: "Sora 2 cinematic clips बन रहे हैं…",
  rendering: "Video render हो रहा है…",
  done: "तैयार है!",
  failed: "विफल",
};

const DEFAULT_YT_TITLE = "INDIAN-NEWS-21 — भव्य शुभारंभ | मुख्य संपादक Jakir Momin";
const DEFAULT_YT_DESC = `INDIAN-NEWS-21 — भारत की आवाज़ 🇮🇳

अब शुरू होता है एक नया अध्याय — देश की हर बड़ी खबर, अब आपकी उँगलियों पर।

🎙️ मुख्य संपादक: Jakir Momin
🌐 Website: https://indiannews-21.com
📱 WhatsApp updates के लिए हमारी वेबसाइट पर जुड़ें।

राजनीति, खेल, मनोरंजन, व्यापार — हर विषय पर विश्वसनीय पत्रकारिता।

#IndianNews21 #GrandOpening #HindiNews #भारतकीआवाज़ #BreakingNews`;
const DEFAULT_YT_TAGS = "IndianNews21,GrandOpening,HindiNews,Breaking News,भारत की आवाज़,news channel,Jakir Momin";

export default function PromoVideoPage() {
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  // YouTube OAuth state
  const [ytStatus, setYtStatus] = useState(null); // { client_configured, connected, channel_title, redirect_uri }
  const [ytExchanging, setYtExchanging] = useState(false);

  // Publish modal
  const [publishFor, setPublishFor] = useState(null); // promo doc
  const [ytTitle, setYtTitle] = useState(DEFAULT_YT_TITLE);
  const [ytDesc, setYtDesc] = useState(DEFAULT_YT_DESC);
  const [ytTags, setYtTags] = useState(DEFAULT_YT_TAGS);
  const [ytPrivacy, setYtPrivacy] = useState("public");
  const [ytIncludeThumb, setYtIncludeThumb] = useState(true);
  const [ytPublishing, setYtPublishing] = useState(false);

  const loadHistory = async () => {
    try { const { data } = await api.get("/admin/promo-video/history"); setHistory(data || []); }
    catch (e) { /* silent */ }
  };
  const loadYtStatus = async () => {
    try {
      const { data } = await api.get("/admin/youtube-oauth/status", { params: { origin: window.location.origin } });
      setYtStatus(data);
    } catch (e) { setYtStatus({ client_configured: false, connected: false }); }
  };
  useEffect(() => { loadHistory(); loadYtStatus(); }, []);

  // Handle OAuth callback: URL contains ?code=...&scope=...
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const err = url.searchParams.get("error");
    if (err) {
      toast.error(`YouTube auth cancelled: ${err}`);
      window.history.replaceState({}, "", url.pathname);
      return;
    }
    if (code && !ytExchanging) {
      setYtExchanging(true);
      api.post("/admin/youtube-oauth/exchange", { code, origin: window.location.origin })
        .then(({ data }) => {
          toast.success(`YouTube connect हो गया: ${data.channel_title || "OK"}`);
          loadYtStatus();
        })
        .catch((e) => toast.error(formatError(e)))
        .finally(() => {
          setYtExchanging(false);
          window.history.replaceState({}, "", url.pathname);
        });
    }
  }, []);

  const poll = async (id) => {
    try {
      const { data } = await api.get(`/admin/promo-video/status/${id}`);
      setJob(data);
      if (data.status === "done" || data.status === "failed") {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setBusy(false);
        if (data.status === "done") toast.success("Grand Opening Promo तैयार! 🎉");
        else toast.error(data.message || "Generation failed");
        loadHistory();
      }
    } catch (e) { /* keep polling */ }
  };

  const start = async () => {
    if (busy) return;
    setBusy(true); setJob(null);
    try {
      const { data } = await api.post("/admin/promo-video/generate");
      setJobId(data.job_id);
      setJob({ job_id: data.job_id, status: "queued", progress: 0, message: "क़तार में…" });
      toast.info("Generation शुरू — 5-10 min लगेंगे (Sora 2 clips)");
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => poll(data.job_id), 5000);
    } catch (e) { setBusy(false); toast.error(formatError(e)); }
  };
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const doDelete = async (id) => {
    if (!confirm("यह promo video delete करें?")) return;
    try {
      await api.delete(`/admin/promo-video/${id}`);
      toast.success("Delete हो गया");
      loadHistory();
      if (jobId === id) { setJob(null); setJobId(null); }
    } catch (e) { toast.error(formatError(e)); }
  };

  const shareWhatsApp = (url) => {
    const fullUrl = url.startsWith("http") ? url : `${fileHost()}${url}`;
    const text = `🎬 INDIAN-NEWS-21 — भव्य शुभारंभ!\nमुख्य संपादक: जकीर मोमिन\n\n${fullUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const connectYouTube = async () => {
    try {
      const { data } = await api.get("/admin/youtube-oauth/auth-url", { params: { origin: window.location.origin } });
      window.location.href = data.auth_url;
    } catch (e) { toast.error(formatError(e)); }
  };
  const disconnectYouTube = async () => {
    if (!confirm("YouTube disconnect करें?")) return;
    try {
      await api.post("/admin/youtube-oauth/disconnect");
      toast.success("Disconnected");
      loadYtStatus();
    } catch (e) { toast.error(formatError(e)); }
  };

  const openPublish = (h) => {
    setPublishFor(h);
    setYtTitle(DEFAULT_YT_TITLE);
    setYtDesc(DEFAULT_YT_DESC);
    setYtTags(DEFAULT_YT_TAGS);
    setYtPrivacy("public");
    setYtIncludeThumb(!!h.thumbnail_url);
  };
  const doPublish = async () => {
    if (!publishFor) return;
    setYtPublishing(true);
    try {
      const { data } = await api.post(`/admin/promo-video/${publishFor.job_id}/publish-youtube`, {
        title: ytTitle,
        description: ytDesc,
        tags: ytTags.split(",").map((t) => t.trim()).filter(Boolean),
        privacy: ytPrivacy,
        include_thumbnail: ytIncludeThumb,
      });
      toast.success("YouTube पर publish हो गया! 🚀");
      window.open(data.youtube_url, "_blank");
      setPublishFor(null);
      loadHistory();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setYtPublishing(false);
    }
  };

  const doneVideo = job?.status === "done" ? job : null;

  return (
    <div className="space-y-6" data-testid="promo-video-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 grid place-items-center shadow-lg">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Grand Opening Promo Video</h1>
          <p className="text-sm text-slate-500">Sora 2 cinematic clips + Onyx Hindi voiceover + INDIAN-NEWS-21 branding</p>
        </div>
      </div>

      {/* YouTube connect card */}
      <div data-testid="yt-connect-card" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm flex flex-wrap items-center gap-3">
        <Youtube className="w-6 h-6 text-red-600" />
        {!ytStatus ? (
          <span className="text-sm text-slate-500">Checking YouTube status…</span>
        ) : !ytStatus.client_configured ? (
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">YouTube upload — setup pending</div>
            <div className="text-xs text-slate-500">Server admin needs to add <code className="px-1 py-0.5 bg-slate-100 rounded">YOUTUBE_OAUTH_CLIENT_ID</code> + <code className="px-1 py-0.5 bg-slate-100 rounded">YOUTUBE_OAUTH_CLIENT_SECRET</code> in backend .env</div>
          </div>
        ) : ytStatus.connected ? (
          <>
            <div className="flex-1">
              <div className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Connected
                {ytStatus.channel_title && <span className="text-slate-700">· {ytStatus.channel_title}</span>}
              </div>
              <div className="text-xs text-slate-500">Ab aap kisi bhi promo video ko seedhe YouTube par publish kar sakte ho.</div>
            </div>
            <Button data-testid="yt-disconnect-btn" variant="outline" onClick={disconnectYouTube} className="text-red-700 border-red-200 hover:bg-red-50">
              <Unlink className="w-4 h-4 mr-1.5" /> Disconnect
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">YouTube connect करें</div>
              <div className="text-xs text-slate-500">One-click authorise — promo videos seedhe channel par upload honge title/description/thumbnail ke saath.</div>
            </div>
            <Button data-testid="yt-connect-btn" onClick={connectYouTube} className="bg-red-600 hover:bg-red-700 text-white" disabled={ytExchanging}>
              {ytExchanging ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Connecting…</> : <><Link2 className="w-4 h-4 mr-1.5"/>Connect YouTube</>}
            </Button>
          </>
        )}
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl">
        <div className="absolute inset-0 opacity-20" style={{
          background: "radial-gradient(circle at 20% 20%, #f97316 0%, transparent 50%), radial-gradient(circle at 80% 80%, #dc2626 0%, transparent 50%)"
        }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-widest mb-4">
            <Film className="w-3.5 h-3.5" /> Cinematic · 45 seconds · 1280×720
          </div>
          <h2 className="text-3xl md:text-4xl font-black leading-tight mb-2">
            एक क्लिक में <span className="text-orange-300">भव्य शुभारंभ</span> Promo
          </h2>
          <p className="text-white/80 max-w-2xl mb-6">
            3× Sora 2 clips (TV studio · ON-AIR mic · newsroom energy) + Onyx boss voice-over + INDIAN-NEWS-21 logo + "मुख्य संपादक: JAKIR MOMIN" — सब कुछ automatic।
          </p>
          <Button data-testid="generate-promo-btn" onClick={start} disabled={busy} size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold h-12 px-6">
            {busy ? <><Loader2 className="w-5 h-5 mr-2 animate-spin"/>Generate हो रहा है…</> :
                    <><Sparkles className="w-5 h-5 mr-2"/>Grand Opening Promo बनाओ</>}
          </Button>
          <div className="text-xs text-white/60 mt-3">⏱️ पहला render ~5-10 min लेगा (Sora 2 3 clips parallel)</div>
        </div>
      </div>

      {/* Live status */}
      {job && (
        <div data-testid="promo-job-status" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              {job.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> :
               job.status === "failed" ? <XCircle className="w-5 h-5 text-red-600" /> :
               <Loader2 className="w-5 h-5 text-orange-600 animate-spin" />}
              {STATUS_LABEL[job.status] || job.status}
            </div>
            <span className="text-xs text-slate-500 font-mono">{job.job_id}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full transition-all duration-500 ${job.status === "failed" ? "bg-red-500" : "bg-gradient-to-r from-orange-500 to-rose-500"}`}
              style={{ width: `${job.progress || 0}%` }} />
          </div>
          <div className="text-sm text-slate-600 mt-2">{job.message}</div>

          {doneVideo && (
            <div className="mt-4 space-y-3">
              <video data-testid="promo-video-player" src={`${fileHost()}${doneVideo.video_url}`} controls playsInline className="w-full rounded-lg border border-slate-200" />
              <div className="flex flex-wrap gap-2">
                <a data-testid="promo-download-btn" href={`${fileHost()}${doneVideo.video_url}`}
                  download={`INDIAN-NEWS-21-Grand-Opening-${doneVideo.job_id}.mp4`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold">
                  <Download className="w-4 h-4" /> Download MP4
                </a>
                <Button data-testid="promo-share-wa-btn" onClick={() => shareWhatsApp(doneVideo.video_url)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Share2 className="w-4 h-4 mr-2" /> WhatsApp
                </Button>
                {ytStatus?.connected ? (
                  <Button data-testid="promo-yt-publish-btn" onClick={() => openPublish(doneVideo)} className="bg-red-600 hover:bg-red-700 text-white">
                    <Youtube className="w-4 h-4 mr-2" /> Publish to YouTube
                  </Button>
                ) : (
                  <Button variant="outline" onClick={connectYouTube} className="border-red-200 text-red-700 hover:bg-red-50">
                    <Youtube className="w-4 h-4 mr-2" /> Connect YouTube to publish
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Clock className="w-5 h-5"/>पिछले Promos</h3>
          <span className="text-xs text-slate-500">{history.length} generated</span>
        </div>
        {history.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">अभी तक कोई promo generate नहीं हुआ</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {history.map((h) => (
              <div key={h.job_id} data-testid={`promo-history-${h.job_id}`} className="rounded-lg border border-slate-200 p-3 hover:shadow-md transition">
                <div className="flex items-center gap-2 mb-2">
                  <PlayCircle className={`w-4 h-4 ${h.status === "done" ? "text-emerald-600" : h.status === "failed" ? "text-red-500" : "text-orange-500"}`} />
                  <div className="text-sm font-semibold text-slate-800 flex-1 truncate">
                    {h.status === "done" ? `Promo · ${h.size_mb || "?"} MB` : STATUS_LABEL[h.status] || h.status}
                  </div>
                  {h.youtube_url && (
                    <a href={h.youtube_url} target="_blank" rel="noreferrer" className="text-red-600 hover:text-red-700" title="View on YouTube">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => doDelete(h.job_id)} data-testid={`promo-delete-${h.job_id}`} className="text-slate-400 hover:text-red-600" aria-label="Delete">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
                {h.video_url ? (
                  <video src={`${fileHost()}${h.video_url}`} controls playsInline className="w-full rounded" />
                ) : (
                  <div className="h-24 rounded bg-slate-50 grid place-items-center text-xs text-slate-400">{h.message}</div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {h.video_url && (
                    <>
                      <a href={`${fileHost()}${h.video_url}`} download={`INDIAN-NEWS-21-${h.job_id}.mp4`}
                        className="text-xs px-2.5 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
                        <Download className="w-3 h-3"/>Download
                      </a>
                      <button onClick={() => shareWhatsApp(h.video_url)}
                        className="text-xs px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1">
                        <Share2 className="w-3 h-3"/>WhatsApp
                      </button>
                      {h.youtube_url ? (
                        <a href={h.youtube_url} target="_blank" rel="noreferrer"
                          className="text-xs px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1">
                          <Youtube className="w-3 h-3"/>View on YT
                        </a>
                      ) : ytStatus?.connected ? (
                        <button data-testid={`yt-publish-${h.job_id}`} onClick={() => openPublish(h)}
                          className="text-xs px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1">
                          <Youtube className="w-3 h-3"/>Publish YT
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-2 font-mono">{h.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Publish modal */}
      {publishFor && (
        <div data-testid="yt-publish-modal" className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => !ytPublishing && setPublishFor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center gap-2">
              <Youtube className="w-6 h-6 text-red-600" />
              <div>
                <div className="font-bold text-slate-900">Publish to YouTube</div>
                <div className="text-xs text-slate-500">Channel: {ytStatus?.channel_title || "connected"}</div>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Title (max 100)</label>
                <Input data-testid="yt-title-input" value={ytTitle} onChange={(e) => setYtTitle(e.target.value.slice(0, 100))} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Description</label>
                <Textarea data-testid="yt-desc-input" value={ytDesc} onChange={(e) => setYtDesc(e.target.value)} rows={8} className="mt-1 font-mono text-xs" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Tags (comma-separated)</label>
                <Input data-testid="yt-tags-input" value={ytTags} onChange={(e) => setYtTags(e.target.value)} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Privacy</label>
                  <select data-testid="yt-privacy-select" value={ytPrivacy} onChange={(e) => setYtPrivacy(e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                    <option value="public">🌐 Public — सब देख सकें</option>
                    <option value="unlisted">🔗 Unlisted — sirf link वाले</option>
                    <option value="private">🔒 Private — sirf mai</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input data-testid="yt-thumb-toggle" type="checkbox" checked={ytIncludeThumb} onChange={(e) => setYtIncludeThumb(e.target.checked)} disabled={!publishFor?.thumbnail_url} className="w-4 h-4"/>
                    Auto-set thumbnail {!publishFor?.thumbnail_url && <span className="text-xs text-slate-400">(N/A)</span>}
                  </label>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
              <Button variant="outline" onClick={() => setPublishFor(null)} disabled={ytPublishing}>Cancel</Button>
              <Button data-testid="yt-publish-submit-btn" onClick={doPublish} disabled={ytPublishing || !ytTitle.trim()} className="bg-red-600 hover:bg-red-700 text-white">
                {ytPublishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Uploading…</> : <><Youtube className="w-4 h-4 mr-2"/>Publish Now</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
